var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { getDataValue, removeDataValue, setDataValue } from '@juvare/json-data-util';
import SearchIcon from '@mui/icons-material/Search';
import { InputAdornment, TextField } from '@mui/material';
import React from 'react';
import { useDebounce, usePropChange } from '../../hooks';
import { removeValuesFromComponentDependencies } from '../utils/deleteComponentDependencies';
export const QuickSearch = (_a) => {
    var { label, dataPath, filterData, onChangeFilterData, sx, filterDependencies } = _a, props = __rest(_a, ["label", "dataPath", "filterData", "onChangeFilterData", "sx", "filterDependencies"]);
    const normalizedValueFromProps = normalize(getStringValue(filterData, dataPath));
    const [inputValue, setInputValue] = React.useState(normalizedValueFromProps);
    const applyValue = React.useCallback((debouncedValue) => {
        const newNormalizedValue = normalize(debouncedValue);
        if (newNormalizedValue !== normalizedValueFromProps) {
            if (newNormalizedValue !== '') {
                onChangeFilterData((prevFilterData) => setStringValue(prevFilterData, dataPath, newNormalizedValue, filterDependencies));
            }
            else {
                onChangeFilterData((prevFilterData) => setStringValue(prevFilterData, dataPath, undefined, filterDependencies));
            }
        }
    }, [dataPath, normalizedValueFromProps, onChangeFilterData, filterDependencies]);
    useDebounce(inputValue, React.useCallback((debouncedValue) => {
        applyValue(debouncedValue);
    }, [applyValue]), 1000);
    usePropChange(normalizedValueFromProps, React.useCallback((newValue) => {
        if (normalize(inputValue) !== newValue) {
            setInputValue(normalize(newValue));
        }
    }, [inputValue]));
    return (React.createElement(TextField, { variant: 'outlined', size: 'small', label: '', fullWidth: true, placeholder: label, value: inputValue, onChange: (event) => setInputValue(event.target.value), onKeyDown: (event) => {
            if (event.key === 'Enter') {
                applyValue(inputValue);
            }
        }, InputProps: {
            startAdornment: (React.createElement(InputAdornment, { position: 'start' },
                React.createElement(SearchIcon, null))),
            endAdornment: props.endAdornment,
        }, inputProps: {
            'aria-label': label,
            'data-test': props['data-test'],
        }, sx: [
            {
                flex: { xs: '1 1 0', lg: '2 1 0', xl: '1.5 1 0' },
                '& input.MuiOutlinedInput-input.MuiInputBase-inputSizeSmall': {
                    // adding 2px to so that height is 42px instead of 40px
                    paddingTop: '12.25px', // magic value = 11.25px (value from JuvareTheme) + 1px
                    paddingBottom: '12.25px', // magic value = 11.25px (value from JuvareTheme) + 1px
                },
                '& input::placeholder': {
                    typography: 'body2',
                    color: themeColors.text.subtle,
                    opacity: 1,
                    textOverflow: 'ellipsis',
                },
            },
            ...(Array.isArray(sx) ? sx : [sx]),
        ] }));
};
function getStringValue(data, dataPath) {
    const value = data ? getDataValue(data, dataPath) : undefined;
    if (typeof value === 'string') {
        return value;
    }
    else {
        return '';
    }
}
function setStringValue(data, dataPath, value, dependencyMap) {
    const newData = JSON.parse(JSON.stringify(data !== null && data !== void 0 ? data : {}));
    if (value) {
        setDataValue(newData, dataPath, value);
    }
    else {
        removeDataValue(newData, dataPath);
        removeValuesFromComponentDependencies(newData, dataPath, dependencyMap);
    }
    return newData;
}
export function normalize(value) {
    const string = typeof value === 'string' ? value : '';
    return string.trim().replace(/\s+/g, ' ');
}
